import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ExternalLink, X, Copy } from "react-feather";

const InAppBrowserDetector = () => {
  const { t } = useTranslation();
  const [isInAppBrowser, setIsInAppBrowser] = useState(false);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const detectInAppBrowser = () => {
      const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;

      const inAppBrowserPatterns = [
        // Facebook Messenger
        /FBAN|FBAV/i,
        // Instagram
        /Instagram/i,
        // WhatsApp
        /WhatsApp/i,
        // Telegram
        /Telegram/i,
        // Zalo
        /Zalo/i,
        // Line
        /Line/i,
        // WeChat
        /MicroMessenger/i,
        // TikTok
        /TikTok/i,
        // Twitter
        /TwitterAndroid/i,
        // LinkedIn
        /LinkedInApp/i,
        // Pinterest
        /Pinterest/i,
        // Snapchat
        /Snapchat/i,
        // Discord
        /Discord/i,
        // Slack
        /Slack/i,
        // Microsoft Teams
        /Teams/i,
        // Zoom
        /Zoom/i,
        // Generic in-app browser detection
        /wv\)/i, // Android WebView
      ];

      const isInApp = inAppBrowserPatterns.some(pattern => pattern.test(userAgent));
      
      const hasInAppFeatures = 
        (!(window as any).chrome || (window as any).chrome.runtime === undefined) &&
        (navigator as any).standalone === false &&
        (window as any).external !== undefined;

      const isDetected = isInApp || hasInAppFeatures;
      
      setIsInAppBrowser(isDetected);
      
      const urlParams = new URLSearchParams(window.location.search);
      const isTestMode = urlParams.get('test-inapp') === '1';
      
      if ((isDetected || isTestMode) && !localStorage.getItem('inAppBrowserBannerDismissed')) {
        setShowBanner(true);
      }
    };

    detectInAppBrowser();
  }, []);

  const handleOpenInExternalBrowser = () => {
    const currentUrl = window.location.href;
    
    // Tạo intent URL cho Android
    const androidIntent = `intent://${window.location.host}${window.location.pathname}${window.location.search}#Intent;scheme=https;package=com.android.chrome;end`;
    
    // Thử mở bằng intent trước (Android)
    try {
      window.location.href = androidIntent;
    } catch (error) {
      // Nếu không thành công, thử mở bằng window.open
      try {
        window.open(currentUrl, '_blank');
      } catch (error) {
        // Fallback: copy URL to clipboard
        navigator.clipboard.writeText(currentUrl).then(() => {
          alert(t('inAppBrowser.urlCopied'));
        });
      }
    }
  };

  const handleDismissBanner = () => {
    setShowBanner(false);
    localStorage.setItem('inAppBrowserBannerDismissed', 'true');
  };

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      alert(t('inAppBrowser.urlCopied'));
    } catch (error) {
      // Fallback cho trình duyệt không hỗ trợ clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = window.location.href;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert(t('inAppBrowser.urlCopied'));
    }
  };

  // Thêm padding-top cho body khi banner hiển thị
  useEffect(() => {
    if (showBanner) {
      document.body.style.paddingTop = '60px';
    } else {
      document.body.style.paddingTop = '0px';
    }
    
    // Cleanup khi component unmount
    return () => {
      document.body.style.paddingTop = '0px';
    };
  }, [showBanner]);

  if (!isInAppBrowser || !showBanner) {
    return null;
  }

  return (
    <div className="fixed top-0 left-0 right-0 bg-yellow-100 dark:bg-yellow-900 border-b border-yellow-200 dark:border-yellow-700 px-4 py-2 z-[9999]">
      <div className="max-w-[1440px] mx-auto flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <div className="text-xs text-yellow-800 dark:text-yellow-200">
            <p className="font-medium">{t('inAppBrowser.title')}</p>
            <p className="text-xs mt-0.5">{t('inAppBrowser.description')}</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleOpenInExternalBrowser}
            className="text-yellow-600 dark:text-yellow-400 hover:text-yellow-800 dark:hover:text-yellow-200 transition-colors"
            title={t('inAppBrowser.openInBrowser')}
          >
            <ExternalLink className="w-4 h-4" />
          </button>
          <button
            onClick={handleCopyUrl}
            className="text-yellow-600 dark:text-yellow-400 hover:text-yellow-800 dark:hover:text-yellow-200 transition-colors"
            title={t('inAppBrowser.copyUrl')}
          >
            <Copy className="w-4 h-4" />
          </button>
          <button
            onClick={handleDismissBanner}
            className="text-yellow-600 dark:text-yellow-400 hover:text-yellow-800 dark:hover:text-yellow-200 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default InAppBrowserDetector;
